import { LogOut, Menu, Users } from 'lucide-react';
import { useNavigate } from 'react-router';

import { useCurrentUser, useSignOut } from '@chartai/api-client';
import { authEventService } from '@chartai/firebase';

import chartaiLogoLight from '@/assets/chartai-logo-light.svg';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/DropdownMenu';
import { useSidebarStore } from '@/shared/stores/sidebarStore';
import { useProfileStore } from '@/shared/stores/useProfileStore';

import { HelpGuideButton } from './HelpGuideButton';

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

export function TopBar() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { mutate: signOut } = useSignOut();
  const openMobile = useSidebarStore((s) => s.openMobile);
  const selectedProfile = useProfileStore((s) => s.selectedProfile);

  const profileName = selectedProfile?.displayName ?? user?.displayName ?? 'User';
  const initials = getInitials(profileName);

  const handleSignOut = () => {
    const email = user?.email ?? '';
    const uid = user?.uid ?? null;
    signOut(undefined, {
      onSuccess: () => {
        const apiUrl = import.meta.env['VITE_AUTH_EVENT_API_URL'] as string | undefined;
        if (apiUrl) {
          authEventService.logAuthEvent(apiUrl, {
            eventType: 'sign_out',
            email,
            uid,
            success: true,
            errorCode: null,
            errorMessage: null,
            userAgent: navigator.userAgent,
            platform: 'web',
          });
        }
        localStorage.removeItem('selectedProfile');
        window.location.replace('/auth/signin');
      },
    });
  };

  return (
    <div className="flex h-12 shrink-0 items-center justify-between bg-[var(--wf-nav)] px-4">
      {/* Left: mobile menu + logo */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={openMobile}
          className="flex items-center justify-center rounded-md p-1.5 text-white/70 transition-colors hover:text-white md:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
        <img src={chartaiLogoLight} alt="ChartAI" className="h-5 w-auto" />
      </div>

      {/* Right: help + user profile */}
      <div className="flex items-center gap-1">
        <HelpGuideButton />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2.5 rounded-lg p-1 transition-colors hover:bg-white/10"
              aria-label="Profile menu"
            >
              <span className="hidden text-sm font-medium text-white sm:block">{profileName}</span>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-xs font-bold text-white">
                {initials}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {user && (
              <>
                <DropdownMenuItem onClick={() => void navigate('/profiles/select')}>
                  <Users className="mr-2 h-4 w-4" aria-hidden="true" />
                  Switch Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  Sign Out
                </DropdownMenuItem>
              </>
            )}
            {!user && (
              <DropdownMenuItem onClick={() => void navigate('/auth/signin')}>
                <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                Sign In
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
